import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CommandPalette from './index';

interface CommandPaletteContextValue {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue>({
  open: () => {},
  close: () => {},
  isOpen: false,
});

/** Lets the topbar button and the mobile header search icon open the palette. */
export const useCommandPalette = () => useContext(CommandPaletteContext);

/**
 * Mounts the palette once and owns the global ⌘K / Ctrl+K binding.
 *
 * The shortcut is deliberately swallowed even while a field has focus —
 * ⌘K is a jump, and having to click out of an input first would defeat it.
 * The one exception is the palette's own input, which handles Escape itself.
 */
export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette open={isOpen} onClose={close} />
    </CommandPaletteContext.Provider>
  );
};

export default CommandPaletteProvider;
